import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Floating iceberg-blue back-to-bottom arrow.
 * Anchored to the top-right corner of the dark results panel, hugging its
 * right edge, per the operator mock "Back to bottom button.pdf".
 * Shows only when a result exists and the user is not at the bottom.
 * Hides while the user actively scrolls, then reappears once scrolling stops.
 */
const BOTTOM_THRESHOLD_PX = 120;
const SCROLL_IDLE_MS = 160;
const HEADER_CLEARANCE_PX = 88;
const PANEL_SELECTOR = '[data-testid^="result-container-"]';

export const BackToBottomButton = ({ enabled = false, onScrollToBottom }) => {
  const [pos, setPos] = useState(null);
  const [show, setShow] = useState(false);
  const scrollTimer = useRef(null);

  const atBottom = () => {
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    const viewport = window.innerHeight;
    const full = document.documentElement.scrollHeight;
    return (full - (scrollY + viewport)) <= BOTTOM_THRESHOLD_PX;
  };

  const computePosition = useCallback(() => {
    const panels = document.querySelectorAll(PANEL_SELECTOR);
    if (!panels.length) return null;
    const rect = panels[panels.length - 1].getBoundingClientRect();
    const top = Math.min(Math.max(rect.top, HEADER_CLEARANCE_PX), window.innerHeight - HEADER_CLEARANCE_PX);
    return { top, left: rect.right };
  }, []);

  const settle = useCallback(() => {
    if (!enabled || atBottom()) { setShow(false); return; }
    const p = computePosition();
    if (!p) { setShow(false); return; }
    setPos(p);
    setShow(true);
  }, [enabled, computePosition]);

  useEffect(() => {
    if (!enabled) { setShow(false); return; }
    const onScroll = () => {
      setShow(false);
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(settle, SCROLL_IDLE_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', settle);
    const initial = setTimeout(settle, 300);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', settle);
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      clearTimeout(initial);
    };
  }, [enabled, settle]);

  if (!enabled || !show || !pos) return null;

  return (
    <button
      type="button"
      data-testid="back-to-bottom-fab"
      aria-label="Back to bottom"
      onClick={onScrollToBottom}
      className="fixed z-40 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-transform duration-200 hover:scale-110"
      style={{ top: pos.top, left: pos.left, backgroundColor: '#7FD3E8', boxShadow: '0 4px 16px rgba(127, 211, 232, 0.5)' }}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="#08222E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
};
